import React, { useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import type { AssistantActiveRouteContextEnvelope } from '@triedge/domain-core';
import {
  type FamilyDetailCoreTree,
  type StudentsDashboardTree,
} from '@triedge/students-families-domain';
import {
  createObjectTreeModuleContext,
  resolveObjectTreeWarmPlan,
  type ObjectTreeResourceState,
} from '@triedge/rtk-abstractions';
import {
  useDomainRouterQuery,
  useLazyDomainRouterQuery,
} from '../../state/domainRouterHooks';
import {
  studentsFamiliesObjectTreeRegistry,
  type StudentsFamiliesObjectTreeRegistry,
} from './objectTreeRegistry';
import { registerAssistantActiveRouteContext } from '../../components/ai/assistantActiveRouteContextRegistry';

type DetailTabId = 'children' | 'family' | 'tuition' | 'documents' | 'notes';

const resolveAcademicYear = (): string => {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${startYear + 1}`;
};

const resolveRouteTab = (pathname: string): DetailTabId | null => {
  if (pathname.endsWith('/children')) return 'children';
  if (pathname.endsWith('/family')) return 'family';
  if (pathname.endsWith('/tuition')) return 'tuition';
  if (pathname.endsWith('/documents')) return 'documents';
  if (pathname.endsWith('/notes')) return 'notes';
  return null;
};

const readErrorMessage = (error: unknown): string | null => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return 'Object-tree query failed.';
};

const asErrorLike = (message: string | null) => (
  message ? { message } : null
);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

const readText = (value: unknown, fallback = 'Unknown'): string => {
  const normalized = String(value ?? '').trim();
  return normalized.length > 0 ? normalized : fallback;
};

export const buildStudentsFamiliesActiveRouteContextEnvelope = (input: {
  pathname: string;
  familyId: string | null;
  activeTab: DetailTabId | null;
  familyCore: FamilyDetailCoreTree | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
}): AssistantActiveRouteContextEnvelope | null => {
  if (!input.familyId) {
    return null;
  }

  const familyRecord = asRecord(input.familyCore?.family);
  const familyName = readText(
    familyRecord.familyName,
    readText(familyRecord.displayName, input.familyId),
  );
  const studentCount = input.familyCore?.students?.length ?? 0;
  const contactCount = input.familyCore?.parentContacts?.length ?? 0;
  const communicationCount = input.familyCore?.communications?.length ?? 0;
  const loadState = input.error
    ? 'error'
    : input.familyCore
      ? 'ready'
      : input.isLoading || input.isFetching
        ? 'loading'
        : 'empty';

  return {
    schemaVersion: '1.0.0',
    contextRef: `ystnj:students-families:family:${input.familyId}`,
    routeFamily: 'students-families',
    pathname: input.pathname,
    revision: [
      input.familyId,
      input.activeTab ?? 'detail',
      loadState,
      familyName,
      studentCount,
      contactCount,
      communicationCount,
    ].join(':'),
    facets: [
      {
        kind: 'selection',
        label: 'Selected family',
        summary: familyName,
        available: true,
        sourceRef: 'domainStudentsFamiliesApi.getFamilyDetailCoreTree',
        state: {
          entityKind: 'family',
          entityId: input.familyId,
          familyId: input.familyId,
          activeTab: input.activeTab,
        },
      },
    ],
    artifactSources: [],
    capabilityConstraints: {
      allowedCapabilityIds: [],
      disabledCapabilityIds: [],
      allowedPolicyEventTypes: [],
      disabledPolicyEventTypes: [],
      allowLocalMutation: false,
      allowArtifactWorkspaceOpen: false,
      allowExport: false,
    },
    visibleStateSummary: {
      title: familyName,
      summary: input.familyCore
        ? `${familyName} family profile: ${studentCount} student records, ${contactCount} parent contacts, ${communicationCount} recent communications.`
        : `Family profile route for ${input.familyId}.`,
      metrics: [
        { label: 'Students', value: String(studentCount) },
        { label: 'Contacts', value: String(contactCount) },
        { label: 'Communications', value: String(communicationCount) },
        { label: 'Active tab', value: input.activeTab ?? 'family' },
      ],
    },
    staleness: {
      state: input.error ? 'stale' : input.familyCore ? 'fresh' : 'unknown',
      reason: readErrorMessage(input.error) ?? undefined,
    },
    sourceRefs: [
      {
        sourceRef: 'domainStudentsFamiliesApi.getFamilyDetailCoreTree',
        kind: 'domain-router-query',
        label: 'Family detail core tree',
      },
    ],
  };
};

type QueryFacade<TData> = {
  data: TData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: { message: string } | null;
  refetch: () => Promise<unknown>;
};

type StudentsFamiliesObjectTreeContextValue = {
  registry: StudentsFamiliesObjectTreeRegistry;
  route: {
    familyId: string | null;
    academicYear: string;
    activeTab: DetailTabId | null;
    isDashboardRoute: boolean;
  };
  resources: {
    dashboard: ObjectTreeResourceState<
      { limit?: number; offset?: number },
      StudentsDashboardTree,
      StudentsDashboardTree
    > & { refetch: () => Promise<unknown> };
    familyCore: ObjectTreeResourceState<
      { familyId: string },
      FamilyDetailCoreTree,
      FamilyDetailCoreTree
    > & { refetch: () => Promise<unknown> };
  };
  warmFamilyRoute: (familyId: string) => Promise<void>;
};

const {
  Provider: StudentsFamiliesObjectTreeContextProvider,
  createSelectorHook,
  useModuleState,
} = createObjectTreeModuleContext<StudentsFamiliesObjectTreeContextValue>('StudentsFamilies');

const buildResourceState = <TArgs, TPayload>(options: {
  key: string;
  args: TArgs | null;
  payload: TPayload | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  scope: 'lazy' | 'page' | 'app' | 'persona';
  refetch: () => Promise<unknown>;
}) => ({
  key: options.key,
  args: options.args,
  payload: options.payload ?? null,
  view: options.payload ?? null,
  isLoading: options.isLoading,
  isFetching: options.isFetching,
  error: readErrorMessage(options.error),
  scope: options.scope,
  refetch: options.refetch,
});

export const StudentsFamiliesObjectTreeProvider = () => {
  const location = useLocation();
  const { familyId: rawFamilyId } = useParams<{ familyId?: string }>();
  const familyId = String(rawFamilyId ?? '').trim() || null;
  const activeTab = resolveRouteTab(location.pathname);
  const academicYear = useMemo(resolveAcademicYear, []);
  const isDashboardRoute = location.pathname.endsWith('/school/students/dashboard');
  const [warmQuery] = useLazyDomainRouterQuery();

  const dashboardQuery = useDomainRouterQuery(
    'getStudentsDashboardTree',
    { limit: 200, offset: 0 },
    { skip: !isDashboardRoute },
  );
  const familyCoreQuery = useDomainRouterQuery(
    'getFamilyDetailCoreTree',
    { familyId: familyId ?? '' },
    { skip: !familyId },
  );
  const activeRouteContext = useMemo(
    () => buildStudentsFamiliesActiveRouteContextEnvelope({
      pathname: location.pathname,
      familyId,
      activeTab,
      familyCore: familyCoreQuery.data as FamilyDetailCoreTree | undefined,
      isLoading: familyCoreQuery.isLoading,
      isFetching: familyCoreQuery.isFetching,
      error: familyCoreQuery.error,
    }),
    [
      activeTab,
      familyCoreQuery.data,
      familyCoreQuery.error,
      familyCoreQuery.isFetching,
      familyCoreQuery.isLoading,
      familyId,
      location.pathname,
    ],
  );

  useEffect(() => registerAssistantActiveRouteContext(activeRouteContext), [activeRouteContext]);

  const warmFamilyRoute = useCallback(async (nextFamilyId: string) => {
    const plan = resolveObjectTreeWarmPlan(
      studentsFamiliesObjectTreeRegistry,
      'page',
      ['familyCore'],
    );
    for (const entry of plan) {
      if (entry.key === 'familyCore') {
        await warmQuery('getFamilyDetailCoreTree', { familyId: nextFamilyId }, true);
      }
    }
  }, [warmQuery]);

  const value = useMemo<StudentsFamiliesObjectTreeContextValue>(() => ({
    registry: studentsFamiliesObjectTreeRegistry,
    route: {
      familyId,
      academicYear,
      activeTab,
      isDashboardRoute,
    },
    resources: {
      dashboard: buildResourceState({
        key: 'students.dashboard',
        args: { limit: 200, offset: 0 },
        payload: dashboardQuery.data as StudentsDashboardTree | undefined,
        isLoading: dashboardQuery.isLoading,
        isFetching: dashboardQuery.isFetching,
        error: dashboardQuery.error,
        scope: 'page',
        refetch: dashboardQuery.refetch,
      }),
      familyCore: buildResourceState({
        key: 'students.family.core',
        args: familyId ? { familyId } : null,
        payload: familyCoreQuery.data as FamilyDetailCoreTree | undefined,
        isLoading: familyCoreQuery.isLoading,
        isFetching: familyCoreQuery.isFetching,
        error: familyCoreQuery.error,
        scope: familyId ? 'page' : 'lazy',
        refetch: familyCoreQuery.refetch,
      }),
    },
    warmFamilyRoute,
  }), [
    activeTab,
    dashboardQuery.data,
    dashboardQuery.error,
    dashboardQuery.isFetching,
    dashboardQuery.isLoading,
    dashboardQuery.refetch,
    familyCoreQuery.data,
    familyCoreQuery.error,
    familyCoreQuery.isFetching,
    familyCoreQuery.isLoading,
    familyCoreQuery.refetch,
    familyId,
    isDashboardRoute,
    warmFamilyRoute,
  ]);

  return (
    <StudentsFamiliesObjectTreeContextProvider value={value}>
      <Outlet />
    </StudentsFamiliesObjectTreeContextProvider>
  );
};

export const useStudentsFamiliesObjectTree = useModuleState;
export const useStudentsFamiliesWarmFamilyRoute = createSelectorHook(
  (state) => state.warmFamilyRoute,
);

const toFamiliesConnection = (
  resource: StudentsFamiliesObjectTreeContextValue['resources']['dashboard'],
): QueryFacade<{ items: unknown[]; totalCount: number }> => ({
  data: resource.view
    ? {
        items: resource.view.families.map((preview) => preview.family),
        totalCount: resource.view.totalCount,
      }
    : undefined,
  isLoading: resource.isLoading,
  isFetching: resource.isFetching,
  error: asErrorLike(resource.error),
  refetch: resource.refetch,
});

const toFamilyPreviewDetail = (
  resource: StudentsFamiliesObjectTreeContextValue['resources']['dashboard'],
  familyId: string,
): QueryFacade<{ students: unknown[] }> => {
  const preview = resource.view?.families.find((entry) => entry.family.id === familyId) ?? null;
  return {
    data: preview ? { students: preview.students } : undefined,
    isLoading: resource.isLoading,
    isFetching: resource.isFetching,
    error: asErrorLike(resource.error),
    refetch: resource.refetch,
  };
};

const toFamilyCoreQuery = (
  resource: StudentsFamiliesObjectTreeContextValue['resources']['familyCore'],
): QueryFacade<{ family: unknown; students: unknown[]; parentContacts: unknown[] }> => ({
  data: resource.view
    ? {
        family: resource.view.family,
        students: resource.view.students,
        parentContacts: resource.view.parentContacts,
      }
    : undefined,
  isLoading: resource.isLoading,
  isFetching: resource.isFetching,
  error: asErrorLike(resource.error),
  refetch: resource.refetch,
});

const toCommunicationsQuery = (
  resource: StudentsFamiliesObjectTreeContextValue['resources']['familyCore'],
): QueryFacade<{ items: unknown[]; totalCount: number; nextToken: null }> => ({
  data: resource.view
    ? {
        items: resource.view.communications,
        totalCount: resource.view.communications.length,
        nextToken: null,
      }
    : undefined,
  isLoading: resource.isLoading,
  isFetching: resource.isFetching,
  error: asErrorLike(resource.error),
  refetch: resource.refetch,
});

export const useStudentsFamiliesDashboardQuery = createSelectorHook(
  (state) => toFamiliesConnection(state.resources.dashboard),
);

export const useStudentsFamiliesDashboardPreviewQuery = (familyId: string) => {
  const state = useStudentsFamiliesObjectTree();
  return toFamilyPreviewDetail(state.resources.dashboard, familyId);
};

export const useStudentsFamiliesFamilyCoreQuery = createSelectorHook(
  (state) => toFamilyCoreQuery(state.resources.familyCore),
);

export const useStudentsFamiliesFamilyCommunicationsQuery = createSelectorHook(
  (state) => toCommunicationsQuery(state.resources.familyCore),
);
